import { ChangeEvent, FC, Fragment, useState } from "react";
// i18next
import { Trans, useTranslation } from "react-i18next";
// Hooks
import useMenu from "app/hooks/useMenu";
import useToggle from "app/hooks/useToggle";
// Redux
import { useAppDispatch } from "app/hooks/useStore";
// Models
import { ICalendarEventType } from "app/models/CalendarEvent";
// Async
import { deleteCalendarEventType } from "app/store/CalendarEventTypes/CalendarEventTypesAsync";
// MUI
import { Box, IconButton, Theme } from "@mui/material";
// Icons
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
// Components
import { Checkbox } from "app/components/Mui";
import ConfirmationDialog from "app/dialogs/ConfirmationDialog";
import EventTypesForm from "./EventTypesForm";

type Props = {
  eventType: ICalendarEventType;
  checked: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

const EventTypeItem:FC<Props> = ({ eventType, checked, onChange }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { Menu, MenuItem, openMenu } = useMenu();
  const { open, toggle } = useToggle();
  const { open:openConfirmationDialog, toggle:toggleConfirmationDialog } = useToggle();

  const handleConfirm = () => {
    setIsLoading(true);
    dispatch(deleteCalendarEventType(eventType.id)).unwrap()
      .finally(() => {
        setIsLoading(false);
        if (openConfirmationDialog) toggleConfirmationDialog();
      });
  }

  return (
    <Fragment>
      <Box sx={{
        px: 2,
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', gap: 2,
        transition: 'background-color 0.3s',
        '&:hover': {
          backgroundColor: '#f5f5f5',
        },
        '&:hover .MuiIconButton-root': {
          opacity: 1
        }
      }}>
        <Checkbox
          size="small"
          name={`event-type-${eventType.id}`}
          value={checked}
          onChange={onChange}
          label={eventType.name}
          sx={{ color: `${eventType.color} !important` }}
        />
        <Box>
          <IconButton
            name={`Event type dropdown ${eventType.id}`}
            aria-label="Event type dropdown button"
            size="small"
            onClick={openMenu}
            sx={{ opacity: 0, transition: 'opacity 0.3s', }}
          ><MoreVertIcon /></IconButton>
          <Menu>
            <MenuItem
              name={`Edit event type ${eventType.id}`}
              onClick={toggle}
            >{t('buttons.edit')}</MenuItem>
            <MenuItem
              name={`Delete event type ${eventType.id}`}
              sx={{
                color: (theme:Theme) => theme.palette.error.main
              }}
              onClick={toggleConfirmationDialog}
            >{t('buttons.delete')}</MenuItem>
          </Menu>
        </Box>
      </Box>

      {open ? (
        <EventTypesForm open={open} onClose={toggle} eventType={eventType} />
      ) : null}
      {openConfirmationDialog ? (
        <ConfirmationDialog
          open={openConfirmationDialog}
          onClose={toggleConfirmationDialog}
          title={t('dialogs.confirmation.deleteTitle', { dataType: t('labels.eventType') })}
          loading={isLoading}
          content={(
            <Trans
              t={t} i18nKey="dialogs.confirmation.deleteConfirmationWithName"
              components={{ strong: <strong /> }}
              values={{ dataType: t('labels.eventType'), dataName: eventType.name }}
            />
          )}
          onConfirm={handleConfirm}
        />
      ) : null}
    </Fragment>
  )
}

export default EventTypeItem;
